/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops UI のモックデータ（Phase 3 のバックエンドが無い段階で UI を組み立てるための fixture）。将来 useOps フックの queryFn を本物の API 呼び出しに差し替えるだけで実データに切り替わる seam
 * @graph-connects none
 */
import type {
  OpsAssignment,
  OpsCategory,
  OpsClassification,
  OpsRequest,
  OpsService,
} from '../types/ops.js';

// サービス表示ラベルは @cortex/ops-core を単一 source とし、ここで再エクスポートする。
export { OPS_SERVICE_LABELS } from '@cortex/ops-core';
import { OPS_SERVICE_SLUGS } from '@cortex/ops-core';

/** キュレーション済みの代表的な依頼（demo.html ベース）。 @graph-connects none */
const CURATED_REQUESTS: OpsRequest[] = [
  {
    id: '8015',
    service: 'aircloset',
    category: '調査依頼',
    urgency: '通常',
    escalated: false,
    state: 'investigating',
    title: 'User cannot proceed to selection after return',
    requesterName: 'yoko',
    assigneeName: 'Ray',
    userIds: ['1892045'],
    submittedAt: '2026-08-14T01:32:00+09:00',
    completedAt: null,
    background:
      'Customer contacted CS on 8/13 saying they cannot see the selection screen after returning items on 8/10. The return was confirmed by warehouse on 8/11 but the user rental_status is still WAITING_STYLING instead of transitioning to the selection phase.',
    requested:
      'Investigate why status did not transition after return confirmation, and fix if possible so the user can proceed to selection.',
    classification: null,
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1723600000',
    backlogTicketUrl: null,
    closeReport: null,
    aiReport: {
      status: 'conclusive',
      bodyMd:
        'Missing user_request_items record. After return confirmation a new user_request_items should have been created for the next selection round; it is missing, which blocks selection and stylist assignment. Pattern matches #7342, #7677 (known 選定できない cluster).',
    },
    memo: 'Confirmed: user_request_items missing after return flow. Same root cause as #7342.',
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [
      { time: '10:32', actor: 'yoko', text: 'created ticket' },
      { time: '10:32', actor: null, text: 'AI pre-investigation completed' },
      { time: '10:33', actor: null, text: '3 related tickets linked' },
      { time: '10:45', actor: 'Ray', text: 'assigned to self' },
    ],
    relatedIds: ['7342', '7677', '8012'],
  },
  {
    id: '8012',
    service: 'aircloset',
    category: '調査依頼',
    urgency: '通常',
    escalated: false,
    state: 'investigating',
    title: "User can't proceed to selection screen",
    requesterName: 'Sima',
    assigneeName: 'Ray',
    userIds: ['1884120'],
    submittedAt: '2026-08-11T10:05:00+09:00',
    completedAt: null,
    background: 'Same-shaped report as #8015 — user stuck before selection.',
    requested: 'Check whether this is the same 選定できない cluster.',
    classification: null,
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1723300000',
    backlogTicketUrl: null,
    closeReport: null,
    aiReport: null,
    memo: null,
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [{ time: '10:05', actor: 'Sima', text: 'created ticket' }],
    relatedIds: ['8015'],
  },
  {
    id: '8010',
    service: 'mall',
    category: '状態変更',
    urgency: '通常',
    escalated: false,
    state: 'open',
    title: 'NP payment stuck in PENDING',
    requesterName: 'Ray',
    assigneeName: null,
    userIds: ['2043771'],
    submittedAt: '2026-08-13T14:20:00+09:00',
    completedAt: null,
    background: 'NP後払い の決済が PENDING のまま確定しない。',
    requested: '状態を確認し、必要なら手動で確定へ更新してほしい。',
    classification: null,
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1723500000',
    backlogTicketUrl: null,
    closeReport: null,
    aiReport: null,
    memo: null,
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [{ time: '14:20', actor: 'Billy', text: 'created ticket' }],
    relatedIds: [],
  },
  {
    id: '7998',
    service: 'aircloset',
    category: '調査依頼',
    urgency: '通常',
    escalated: false,
    state: 'blocked',
    title: 'Wrong delivery — items mismatch',
    requesterName: 'Tada',
    assigneeName: 'Ryoma',
    userIds: ['1770994'],
    submittedAt: '2026-08-04T09:10:00+09:00',
    completedAt: null,
    background: 'お客様に別ユーザーの商品が届いた。配送データの取り違えが疑われる。',
    requested: '原因調査と再発防止。至急。',
    classification: null,
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1722700000',
    backlogTicketUrl: null,
    closeReport: null,
    aiReport: {
      status: 'inconclusive',
      bodyMd:
        'Could not determine root cause from delivery logs alone — warehouse scan data for 8/3 is missing. Next step: pull WMS export for shipment batch B-2043 to confirm which label was applied.',
    },
    memo: 'Blocked on WMS export from logistics.',
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [
      { time: '09:10', actor: 'Tada', text: 'created ticket' },
      { time: '09:11', actor: null, text: 'AI pre-investigation completed (inconclusive)' },
      { time: '11:40', actor: 'Ryoma', text: 'marked blocked — waiting on WMS' },
    ],
    relatedIds: [],
  },
  {
    id: '8008',
    service: 'bridge',
    category: '状態変更',
    urgency: '通常',
    escalated: false,
    state: 'closed',
    title: '退会 error — could not cancel membership',
    requesterName: 'yoko',
    assigneeName: 'Ray',
    userIds: ['1655201'],
    submittedAt: '2026-07-25T13:00:00+09:00',
    completedAt: '2026-07-25T15:20:00+09:00',
    background: '退会処理がエラーで完了しなかった。',
    requested: '退会を完了させてほしい。',
    classification: 'user_error',
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1722900000',
    backlogTicketUrl: null,
    closeReport: null,
    aiReport: null,
    memo: 'User had an active rental; guided CS to return-first flow. No system issue.',
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [
      { time: '13:00', actor: 'yoko', text: 'created ticket' },
      { time: '15:20', actor: 'Ray', text: 'closed — user_error' },
    ],
    relatedIds: [],
  },
  {
    id: '8005',
    service: 'dress',
    category: 'データ修正',
    urgency: '通常',
    escalated: false,
    state: 'closed',
    title: 'Coupon applied twice',
    requesterName: 'Sima',
    assigneeName: 'Billy',
    userIds: ['1992310'],
    submittedAt: '2026-07-18T11:30:00+09:00',
    completedAt: '2026-07-18T16:05:00+09:00',
    background: '同一クーポンが二重適用され金額がずれた。',
    requested: '金額の是正。',
    classification: 'bug',
    slackThreadUrl: 'https://air-closet.slack.com/archives/C0DAILYOPS/p1722800000',
    backlogTicketUrl: 'https://air-closet.backlog.com/view/OPS-19201',
    closeReport: null,
    aiReport: null,
    memo: 'Fixed amount via manual correction; filed bug for the double-apply race.',
    point: null,
    pointSource: null,
    wfid: null,
    csTicketUrl: null,
    source: null,
    sourceMetadata: null,
    relatedTickets: [],
    preScan: null,
    notes: [],
    attachments: [],
    activity: [
      { time: '11:30', actor: 'Sima', text: 'created ticket' },
      { time: '16:05', actor: 'Billy', text: 'closed — bug, filed OPS-19201' },
    ],
    relatedIds: [],
  },
];

/** 決定論的にモック配列から index に応じた要素を取る。 @graph-connects none */
function pick<T>(arr: readonly T[], i: number): T {
  return arr[i % arr.length];
}

/** 履歴ブラウズ用の過去クローズ済みモックを決定論的に生成する（無限スクロール確認用）。 @graph-connects none */
function buildHistoricalRequests(): OpsRequest[] {
  const services: readonly OpsService[] = OPS_SERVICE_SLUGS;
  const categories: OpsCategory[] = ['調査依頼', '状態変更', 'データ修正', 'Fatal'];
  const classifications: OpsClassification[] = ['bug', 'spec', 'user_error', 'data', 'dashboard'];
  const titles = [
    'Payment retry stuck',
    'Delivery delayed',
    'Coupon mismatch',
    'Login loop',
    'Return not reflected',
    'Point balance wrong',
    'Address update failed',
    'Subscription paused',
  ];
  const requesters = ['yoko', 'Sima', 'Billy', 'Tada'];
  const engineers = ['Ray', 'Ryoma', 'Billy', 'Zakk'];
  const dayMs = 86_400_000;
  const base = new Date('2026-07-30T10:00:00+09:00').getTime();
  return Array.from(
    { length: 120 },
    (_, i): OpsRequest => ({
      id: String(7896 - i),
      service: pick(services, i),
      category: pick(categories, i),
      urgency: '通常',
      escalated: false,
      state: 'closed',
      title: pick(titles, i),
      requesterName: pick(requesters, i),
      assigneeName: pick(engineers, i),
      userIds: [String(1_500_000 + i * 137)],
      submittedAt: new Date(base - (i + 3) * dayMs).toISOString(),
      completedAt: new Date(base - (i + 1) * dayMs).toISOString(),
      background: '(historical record — archived)',
      requested: '(historical record — archived)',
      classification: pick(classifications, i),
      slackThreadUrl: null,
      backlogTicketUrl: null,
      closeReport: null,
      aiReport: null,
      memo: 'Closed — archived.',
      point: null,
      pointSource: null,
      wfid: null,
      csTicketUrl: null,
      source: null,
      sourceMetadata: null,
      relatedTickets: [],
      preScan: null,
      notes: [],
      attachments: [],
      activity: [],
      relatedIds: [],
    }),
  );
}

/** モック ops 依頼一覧（キュレーション + 生成した履歴）。 @graph-connects none */
export const OPS_REQUESTS: OpsRequest[] = [...CURATED_REQUESTS, ...buildHistoricalRequests()];

/** サービスごとのオペ担 + review_sql 当番のモック割当。 @graph-connects none */
export const OPS_ASSIGNMENTS: OpsAssignment[] = [
  {
    service: 'aircloset',
    serviceLabel: 'airCloset',
    opetanQueue: ['Ray', 'Ryoma'],
    opetanCadence: 'weekly',
    reviewerQueue: ['Ray', 'Ryoma', 'Billy'],
    reviewerCadence: 'weekly',
    covers: [],
  },
  {
    service: 'dress',
    serviceLabel: 'Occasion Dress',
    opetanQueue: ['Lucky', 'Billy'],
    opetanCadence: 'monthly',
    reviewerQueue: ['Lucky', 'Billy', 'Sima'],
    reviewerCadence: 'monthly',
    covers: [],
  },
  {
    service: 'mall',
    serviceLabel: 'aC MALL',
    opetanQueue: ['Billy', 'Tada'],
    opetanCadence: 'weekly',
    reviewerQueue: ['Tada', 'Billy'],
    reviewerCadence: 'weekly',
    covers: [],
  },
  {
    service: 'bridge',
    serviceLabel: 'Bridge',
    opetanQueue: ['Ryoma', 'Ray'],
    opetanCadence: 'weekly',
    reviewerQueue: ['Ryoma', 'Ray'],
    reviewerCadence: 'daily',
    covers: [],
  },
  {
    service: 'mens',
    serviceLabel: "airCloset Men's",
    opetanQueue: ['Zakk', 'Ray'],
    opetanCadence: 'monthly',
    reviewerQueue: ['Zakk', 'Ray'],
    reviewerCadence: 'monthly',
    covers: [],
  },
];

/** 現在サインインしている風のユーザー（自分が起票 / 対応する依頼の絞り込み用）。 @graph-connects none */
export const OPS_CURRENT_USER = 'Ray';
